import React, { useState, useEffect } from "react";
import BusinessDataService from "../services/BusinessService";
import UserDataService from "../services/UserService";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  alpha,
  FormControlLabel,
  Switch
} from "@mui/material";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import BusinessProfitCalculatorDialog, { TENOR_MARGIN_DEFAULTS } from "../../components/BusinessProfitCalculator";
import StampDutyService from "../services/StampDutyService";
import { fNumber } from "../../utils/formatNumber";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const clean = String(val).replace(/,/g, "").trim();
  const n = parseFloat(clean);
  return Number.isFinite(n) ? n : 0;
};

const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts[1].slice(0, 2)}`;
  }
  return intPart;
};

const formatToCurrencyString = (val) => {
  const n = parseNum(val);
  if (n === 0 && (!val || String(val).trim() === "")) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AddBusiness = () => {
  const { owner } = useParams();
  const navigate = useNavigate();

  const initialBusinessState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    costAmount: "",
    profitMarginRate: "10",
    creditOrDebit: "credit",
    tenor: "6",
    description: "",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null
  };

  const [business, setBusiness] = useState(initialBusinessState);
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");
  const [calcOpen, setCalcOpen] = useState(false);
  const [applyStampDuty, setApplyStampDuty] = useState(true);
  const [stampDutyAmount, setStampDutyAmount] = useState("50");
  const [waiveReason, setWaiveReason] = useState("");

  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => { });
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setBusiness(prev => ({ ...prev, [name]: value }));
  };

  // Helper when tenor is selected: auto-set the default profit margin
  const handleTenorSelect = (selectedMonths) => {
    const defaultRate = TENOR_MARGIN_DEFAULTS[selectedMonths] !== undefined
      ? String(TENOR_MARGIN_DEFAULTS[selectedMonths])
      : business.profitMarginRate;

    setBusiness(prev => ({
      ...prev,
      tenor: String(selectedMonths),
      profitMarginRate: defaultRate
    }));
  };

  // Live calculations for Buy transaction
  const cleanCost = parseFloat(String(business.costAmount || "").replace(/,/g, "")) || 0;
  const marginRate = parseFloat(business.profitMarginRate) || 0;
  const tenorMonths = parseInt(business.tenor, 10) || 1;
  const calculatedMarginAmount = cleanCost > 0 ? Number(((cleanCost * marginRate) / 100).toFixed(2)) : 0;
  const calculatedTotalAmount = cleanCost > 0 ? Number((cleanCost + calculatedMarginAmount).toFixed(2)) : 0;
  const calculatedMonthly = tenorMonths > 0 ? Number((calculatedTotalAmount / tenorMonths).toFixed(2)) : 0;

  const handleApplyFromCalculator = ({ costAmount, tenor: t, profitMarginRate: r }) => {
    setBusiness(prev => ({
      ...prev,
      costAmount: String(costAmount),
      tenor: String(t),
      profitMarginRate: String(r),
      amount: String((costAmount + (costAmount * r) / 100).toFixed(2))
    }));
  };

  const saveBusiness = async () => {
    setError("");

    if (business.creditOrDebit === "credit") {
      if (!business.costAmount || cleanCost <= 0) {
        setError("Please enter the purchase amount paid to buy the item for the member.");
        return;
      }
      if (!tenorMonths || tenorMonths <= 0) {
        setError("Please select or enter a valid repayment tenor (e.g. 3, 6, 9 months).");
        return;
      }
    } else {
      // Repayment
      const repaymentAmt = parseFloat(String(business.amount || "").replace(/,/g, ""));
      if (!repaymentAmt || repaymentAmt <= 0) {
        setError("Please enter a valid repayment amount.");
        return;
      }
    }

    setLoading(true);
    try {
      const isBuy = business.creditOrDebit === "credit";
      const finalAmount = isBuy ? calculatedTotalAmount : Number(String(business.amount).replace(/,/g, ""));

      const payload = {
        owner: business.owner,
        date: typeof business.date === "string" ? business.date : toDateInput(business.date),
        creditOrDebit: String(business.creditOrDebit),
        amount: finalAmount,
        costAmount: isBuy ? cleanCost : 0,
        profitMarginRate: isBuy ? marginRate : 0,
        profitMarginAmount: isBuy ? calculatedMarginAmount : 0,
        tenor: isBuy ? tenorMonths : null,
        description: (business.description || "").trim()
      };

      const res = await BusinessDataService.create(business.owner, payload);
      const r = res?.data || {};
      const ownerId = (r.owner && typeof r.owner === "object") ? (r.owner._id || r.owner.id || String(r.owner)) : (r.owner ?? business.owner);

      // Record Stamp Duty or waiver for Business Repayment deposit
      if (business.creditOrDebit === "debit") {
        const dutyNum = applyStampDuty ? (parseNum(stampDutyAmount) || 50) : 0;
        StampDutyService.record({
          memberId: ownerId,
          depositAmount: payload.amount,
          stampDutyAmount: dutyNum,
          accountType: "business",
          isWaived: !applyStampDuty,
          waiveReason: !applyStampDuty ? (waiveReason || "Waived on Business Repayment deposit") : "",
          date: payload.date,
          notes: payload.description || `Business Repayment deposit (Stamp Duty: ₦${dutyNum})`
        }).catch(err => console.error("Stamp duty recording error:", err));
      }

      setSubmitted(true);
      setSnackOpen(true);
      navigate(`/dashboard/business/${encodeURIComponent(ownerId)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newBusiness = () => {
    setBusiness({ ...initialBusinessState, owner: owner || initialBusinessState.owner });
    setSubmitted(false);
    setError("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#0B3B60" }}>
            {business.creditOrDebit === "credit" ? "Record Business Purchase (Buy)" : "Record Business Repayment"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Trade & Commodity Financing • Auto Profit Margin Ledger
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<CalculateRoundedIcon />}
          onClick={() => setCalcOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: "#0D9488",
            borderColor: "#0D9488",
            borderRadius: 2,
            "&:hover": { borderColor: "#0F766E", bgcolor: alpha("#0D9488", 0.08) }
          }}
        >
          Open Profit Calculator
        </Button>
      </Stack>

      {error && (
        <Box sx={{ mb: 2.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Business transaction recorded successfully!
          </Alert>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newBusiness}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={2.5}>
            {/* Owner & Date */}
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Member Name"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Member assigned to this transaction (read-only)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="date"
                name="date"
                label="Transaction Date"
                type="date"
                value={business.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            {/* Transaction Type Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: "#334155" }}>
                Transaction Type *
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant={business.creditOrDebit === "credit" ? "contained" : "outlined"}
                  onClick={() => setBusiness(s => ({ ...s, creditOrDebit: "credit" }))}
                  sx={{
                    minWidth: 160,
                    py: 1.25,
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 2,
                    bgcolor: business.creditOrDebit === "credit" ? "#0D9488" : "transparent",
                    color: business.creditOrDebit === "credit" ? "#FFFFFF" : "#0D9488",
                    borderColor: "#0D9488",
                    "&:hover": { bgcolor: business.creditOrDebit === "credit" ? "#0F766E" : alpha("#0D9488", 0.08) }
                  }}
                >
                  Buy for User (+)
                </Button>
                <Button
                  variant={business.creditOrDebit === "debit" ? "contained" : "outlined"}
                  onClick={() => setBusiness(s => ({ ...s, creditOrDebit: "debit", tenor: "" }))}
                  sx={{
                    minWidth: 160,
                    py: 1.25,
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 2,
                    bgcolor: business.creditOrDebit === "debit" ? "#0B3B60" : "transparent",
                    color: business.creditOrDebit === "debit" ? "#FFFFFF" : "#0B3B60",
                    borderColor: "#0B3B60",
                    "&:hover": { bgcolor: business.creditOrDebit === "debit" ? "#07253D" : alpha("#0B3B60", 0.08) }
                  }}
                >
                  Member Repayment (-)
                </Button>
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
                {business.creditOrDebit === "credit"
                  ? "Buy: Purchase goods on behalf of member. Auto-calculates society profit margin and adds total to member's business debt."
                  : "Repayment: Member pays toward their outstanding business ledger balance."}
              </Typography>
            </Grid>

            {/* BUY SPECIFIC FIELDS */}
            {business.creditOrDebit === "credit" ? (
              <>
                {/* 1. Item Purchase Cost */}
                <Grid item xs={12} md={6}>
                  <TextField
                    id="costAmount"
                    name="costAmount"
                    label="Item Purchase Cost (Amount Spent) *"
                    placeholder="e.g. 150,000.00"
                    type="text"
                    value={business.costAmount}
                    onChange={(e) => {
                      const val = formatInputWithCommas(e.target.value);
                      setBusiness(prev => ({ ...prev, costAmount: val }));
                    }}
                    onBlur={() => {
                      if (business.costAmount) {
                        setBusiness(prev => ({ ...prev, costAmount: formatToCurrencyString(business.costAmount) }));
                      }
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      inputMode: "numeric"
                    }}
                    helperText="Actual capital paid to purchase the goods for the member"
                    required
                  />
                </Grid>

                {/* 2. Profit Margin % */}
                <Grid item xs={12} md={6}>
                  <TextField
                    id="profitMarginRate"
                    name="profitMarginRate"
                    label="Profit Margin % (Auto-filled / Customizable) *"
                    type="number"
                    value={business.profitMarginRate}
                    onChange={handleInputChange}
                    fullWidth
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    helperText="Standard: 5% (3m), 10% (6m), 12% (9m). Officer can customize."
                    required
                  />
                </Grid>

                {/* 3. Tenor Selection Buttons */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: "#0B3B60" }}>
                    Select Repayment Tenor *
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                    {[
                      { m: 3, label: "3 Months (5%)", rate: 5 },
                      { m: 6, label: "6 Months (10%)", rate: 10 },
                      { m: 9, label: "9 Months (12%)", rate: 12 },
                    ].map(({ m, label, rate }) => {
                      const isSelected = String(business.tenor) === String(m);
                      return (
                        <Button
                          key={m}
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => handleTenorSelect(m)}
                          sx={{
                            minWidth: 150,
                            py: 1,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 800,
                            bgcolor: isSelected ? "#0D9488" : "background.paper",
                            color: isSelected ? "#FFFFFF" : "#0D9488",
                            borderColor: "#0D9488",
                            "&:hover": {
                              bgcolor: isSelected ? "#0F766E" : alpha("#0D9488", 0.08)
                            }
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}

                    <TextField
                      id="tenor"
                      name="tenor"
                      label="Custom Months"
                      type="number"
                      size="small"
                      value={business.tenor || ""}
                      onChange={handleInputChange}
                      inputProps={{ min: 1, max: 60 }}
                      sx={{ width: 140 }}
                      helperText="Or type custom tenor"
                    />
                  </Box>
                </Grid>

                {/* Live Preview Card */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: alpha("#059669", 0.05),
                      border: `1.5px solid ${alpha("#059669", 0.25)}`
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <TrendingUpRoundedIcon sx={{ color: "#059669", fontSize: 22 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#059669" }}>
                        Auto-Calculated Margin & Repayment Preview
                      </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                          Item Purchase Cost:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          ₦{fNumber(cleanCost)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                          Profit Margin (+{marginRate}%):
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#059669" }}>
                          +₦{fNumber(calculatedMarginAmount)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                          Total Member Debt:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: "#0B3B60" }}>
                          ₦{fNumber(calculatedTotalAmount)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                          Monthly Repayment ({tenorMonths}m):
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#0D9488" }}>
                          ₦{fNumber(calculatedMonthly)} / mo
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${alpha("#059669", 0.3)}` }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        💡 <strong>Dedicated Profit Ledger:</strong> The <strong>₦{fNumber(calculatedMarginAmount)}</strong> profit margin is segregated into the Business Profit Account on your officer analytics dashboard.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </>
            ) : (
              /* REPAYMENT SPECIFIC FIELD */
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="amount"
                    name="amount"
                    label="Repayment Amount *"
                    placeholder="e.g. 50,000.00"
                    type="text"
                    value={business.amount}
                    onChange={(e) => {
                      const val = formatInputWithCommas(e.target.value);
                      setBusiness(prev => ({ ...prev, amount: val }));
                    }}
                    onBlur={() => {
                      if (business.amount) {
                        setBusiness(prev => ({ ...prev, amount: formatToCurrencyString(business.amount) }));
                      }
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      inputMode: "numeric"
                    }}
                    helperText="Enter member repayment deposit amount"
                    required
                  />
                </Grid>

                {/* STAMP DUTY CONTROL FOR BUSINESS REPAYMENT DEPOSIT */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "#F8FAFC",
                      border: "1px solid #E2E8F0"
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#0D9488" : "#94A3B8" }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                          Stamp Duty (EMTL)
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={applyStampDuty}
                            onChange={(e) => setApplyStampDuty(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={applyStampDuty ? `Apply ₦${stampDutyAmount || 50} Stamp Duty` : "Waive Stamp Duty"}
                        sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem" } }}
                      />
                    </Box>

                    {applyStampDuty ? (
                      <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                          Duty Amount:
                        </Typography>
                        {[50, 100, 200].map((preset) => (
                          <Button
                            key={preset}
                            size="small"
                            variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                            onClick={() => setStampDutyAmount(String(preset))}
                            sx={{
                              minWidth: 64,
                              py: 0.3,
                              px: 1,
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              borderRadius: "6px",
                              bgcolor: String(stampDutyAmount) === String(preset) ? "#0D9488" : "transparent",
                              color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#0D9488",
                              borderColor: "#0D9488"
                            }}
                          >
                            ₦{preset} {preset === 50 ? "(Std)" : ""}
                          </Button>
                        ))}
                        <TextField
                          size="small"
                          type="number"
                          label="Custom Duty (₦)"
                          value={stampDutyAmount}
                          onChange={(e) => setStampDutyAmount(e.target.value)}
                          inputProps={{ min: 0 }}
                          sx={{ width: 140, backgroundColor: "#ffffff" }}
                        />
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Reason for waiving (e.g. Single bulk transfer split across savings/shares/business)"
                        value={waiveReason}
                        onChange={(e) => setWaiveReason(e.target.value)}
                        sx={{ mt: 1.5, backgroundColor: "#ffffff" }}
                      />
                    )}
                  </Paper>
                </Grid>
              </>
            )}

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Transaction Description"
                value={business.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder={business.creditOrDebit === "credit" ? "e.g. Purchase of commercial refrigerator / sewing machines" : "e.g. Month 1 business goods installment repayment"}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 1 }}>
                <Button
                  variant="contained"
                  onClick={saveBusiness}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                  sx={{
                    bgcolor: "#0D9488",
                    fontWeight: 800,
                    px: 4,
                    py: 1.25,
                    borderRadius: 2,
                    "&:hover": { bgcolor: "#0F766E" }
                  }}
                >
                  {business.creditOrDebit === "credit"
                    ? `Save Purchase (₦${fNumber(calculatedTotalAmount)})`
                    : "Save Repayment"}
                </Button>
                <Button variant="outlined" onClick={newBusiness} disabled={loading} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => window.history.back()} sx={{ borderRadius: 2 }}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Embedded Profit Calculator Dialog */}
      <BusinessProfitCalculatorDialog
        open={calcOpen}
        onClose={() => setCalcOpen(false)}
        initialCost={business.costAmount}
        initialTenor={business.tenor}
        initialMarginRate={business.profitMarginRate}
        onApply={handleApplyFromCalculator}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Business transaction saved successfully
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AddBusiness;
